import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { FC, MouseEventHandler } from 'react'
import { Map, Marker, NavigationControl } from 'react-map-gl'
import type { MarkerProps } from 'react-map-gl'
import {
	Box,
	Heading,
	CloseButton,
	Flex,
	Link as ChakraLink,
	Text,
} from '@chakra-ui/react'
import { PinIcn, Tag } from '@alexanderathoodly/ui-library'
import { displayAddress } from '@alexanderathoodly/data-models'
import type { LocationResult } from '@alexanderathoodly/data-models'
import { useTranslation } from 'react-i18next'
import { useMapManager } from '@yta/floorplan'
import { points as turfPoints } from '@turf/helpers'
import turfBBox from '@turf/bbox'
import { ImgProxy } from '~/utils/imgproxy'
import { SIZE_410 } from '@yta/image-proxy'
import { Link } from 'react-router-dom'

type MapProps = {
	locations: LocationResult[]
}

const InteractiveMap: FC<MapProps> = ({ locations }) => {
	const [isMapLoaded, setIsMapLoaded] = useState(false)
	const boundsLike = useMemo(() => {
		if (locations.length == 0) {
			return undefined
		}
		const locationPoints = locations.map(({ address }) => [
			address.coordinates.lng,
			address.coordinates.lat,
		])
		const featureCollection = turfPoints(locationPoints)
		const bounds = turfBBox(featureCollection)
		return {
			west: bounds[0],
			south: bounds[1],
			east: bounds[2],
			north: bounds[3],
		}
	}, [locations])
	const [viewport, setViewport] = useState({
		latitude: boundsLike ? (boundsLike.north + boundsLike.south) / 2 : 59.3253,
		longitude: boundsLike ? (boundsLike.west + boundsLike.east) / 2 : 18.0628,
	})
	const initialViewState = boundsLike
		? {
				bounds: [
					boundsLike.south,
					boundsLike.west,
					boundsLike.north,
					boundsLike.east,
				] as [number, number, number, number],
		  }
		: viewport
	const { ref, fitBounds } = useMapManager({ defaultViewport: viewport })
	useEffect(() => {
		if (boundsLike && isMapLoaded) {
			fitBounds(boundsLike, { padding: 50, animate: true })
		}
	}, [boundsLike, fitBounds, isMapLoaded])

	const [activeLocation, selectLocation] = useState<LocationResult | null>(null)
	const mapCardClickEvent = useCallback(() => {
		selectLocation(null)
	}, [selectLocation])

	const markers = useMemo(
		() =>
			locations.map((location) => (
				<LocationMarker
					key={location.id}
					longitude={location.address.coordinates.lng}
					latitude={location.address.coordinates.lat}
					selected={activeLocation === location}
					onClick={() => selectLocation(location)}
				/>
			)),
		[locations, activeLocation]
	)

	return (
		<Box position="relative" h={{ base: 64, md: 80, lg: 96 }} w="100%">
			<Map
				{...viewport}
				ref={ref}
				onLoad={() => setIsMapLoaded(true)}
				initialViewState={initialViewState}
				onMove={(event) => setViewport(event.viewState)}
				style={{
					width: '100%',
					height: '100%',
				}}
				mapStyle="mapbox://styles/mapbox/light-v9"
				mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
			>
				{markers}
				<NavigationControl position="top-left" showCompass={false} />
				{activeLocation && (
					<LocationCard
						location={activeLocation}
						onClickEvent={mapCardClickEvent}
					/>
				)}
			</Map>
		</Box>
	)
}

export const LocationCard: FC<{
	location: LocationResult
	onClickEvent?: () => void
}> = memo(({ location, onClickEvent }) => {
	const { t } = useTranslation()

	return (
		<Box
			position="absolute"
			bottom={5}
			left={{ base: 1, md: 5 }}
			p={{ base: 5 }}
			w="xs"
			bg="white"
			borderColor="grey.100"
			borderStyle="solid"
			borderWidth="thin"
			borderRadius="base"
			zIndex={5}
		>
			<Flex mb={3} alignItems="center">
				<ChakraLink
					as={Link}
					to={`${t('/kontorshotell', { ns: 'url' })}/${location.url}`}
					target="_blank"
					variant="black"
					flex={1}
					overflow="hidden"
				>
					<Heading
						fontSize={{ base: 'sm', md: 'lg' }}
						fontWeight="semibold"
						whiteSpace="nowrap"
					>
						{location.name}
					</Heading>
				</ChakraLink>
				<CloseButton
					aria-label="Close Solution Card"
					size="md"
					ml={6}
					onClick={onClickEvent}
				/>
			</Flex>
			<Flex alignItems="center">
				<PinIcn
					outer={{
						fill: 'blue.500',
						stroke: 'blue.500',
					}}
					inner={{ fill: 'white' }}
					css={{
						height: '1rem',
						width: '1rem',
						marginRight: '0.25rem',
					}}
				/>
				<Text color="grey.500">
					{displayAddress(location.address).replace(
						',',
						`, ${location.breadcrumbs.area.name}, `
					)}
				</Text>
			</Flex>
			<Flex flexDirection="column" display={{ base: 'none', md: 'unset' }}>
				<Flex gap="0.5rem" flexDirection={{ base: 'column', md: 'row' }}>
					{location.highlights.map((highlight, index) => (
						<Tag
							key={`serpcard-${location.id}-${index}`}
							color="light-blue"
							size="font10"
						>
							{t(highlight)}
						</Tag>
					))}
				</Flex>
				<ImgProxy
					src={location.images[0] || ''}
					sizeSet={{ base: SIZE_410 }}
					alt={`${location.name}`}
					mt={2}
				/>
			</Flex>
		</Box>
	)
})

LocationCard.displayName = 'LocationCard'

type LocationMarkerProps = MarkerProps & {
	onClick?: MouseEventHandler
	selected: boolean
}

const LocationMarker = memo<LocationMarkerProps>(
	({ onClick, selected, ...props }) => {
		const defaultPinStyle = {
			outer: {
				fill: 'blue.500',
				stroke: 'white',
			},
			inner: { fill: 'white' },
		}
		const selectedPinStyle = {
			outer: {
				fill: 'white',
				stroke: 'blue.100',
			},
			inner: { fill: 'blue.500' },
		}
		return (
			<Marker {...props}>
				<Box as="button" onClick={onClick}>
					<PinIcn
						height={10}
						width={7}
						_hover={selectedPinStyle}
						_selected={selectedPinStyle}
						aria-selected={selected}
						outer={defaultPinStyle.outer}
						inner={defaultPinStyle.inner}
					/>
				</Box>
			</Marker>
		)
	}
)

LocationMarker.displayName = 'LocationMarker'

export default InteractiveMap
